import { createAsyncThunk, Dispatch } from "@reduxjs/toolkit";
import { openDocument } from "../../../utils/helpers/OpenDocumentHelper";
import { GenericError } from "../../../utils/exception/Error";
import {
  cloneDocumentPart,
  deleteDocumentRoot,
  detachMultilinkedDocumentPart,
  downloadDocumentCopyJob,
  duplicateDocumentRoot,
  exportDocumentRoot,
  fetchCreatedDocument,
  importDocumentRoot,
} from "../../../api/document/Documents";
import {
  deleteDocumentPart,
  editDocumentPart,
  fetchChangeDocumentPartStructure,
  unlinkDocumentPart,
} from "../../../api/document/DocumentsPart";
import { DEFAULT_FOLDER_CREATION } from "../../../components/document/WithApiFileLocationProvider";
import {
  CLONE_ELABORATION,
  DELETE_OBJECT_NOTIFICATION,
  EDIT_DOCUMENT_PROPERTIES_NOTIFICATION,
  INSERT_OBJECT_NOTIFICATION,
} from "../../../utils/constants/notification";
import {
  deleteXMLPart,
  DocPropKeys,
  editXMLPart,
  getCCByDocPartId,
  retrieveDocumentProp,
} from "../../../utils/helpers/CustomXmlHelper";
import { deleteContentControlById, updateContentControlName } from "../../../office/ContentControl";
import { normalizeDocumentName } from "../../../utils/helpers/ObjectCheckerHelper";
import NotificationManager from "../../../common/notification/NotificationManager";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { DocumentPartMetadata } from "../../../api/models/document-part-metadata";
import { DocumentPart } from "../../../api/models";
import { TgkImpExpElaborationRequest } from "../../../api/models/tgk-imp-exp-elaboration-request";
import * as uuid from "uuid";
import { TgkDuplicateElaborationRequest } from "../../../api/models/tgk-duplicate-elaboration-request";
import { DocumentParameters } from "../../../components/document/ParametersDocument";
import { AdvancedDocumentMetadata, TemplateInfo } from "../../../utils/helpers/DocumentAdvancedCustomXmlHelper";
import {
  getCoObjectDocumentPartInclude,
  getCoObjectListToUpdate,
  getCoObjectsDocumentPartInclude,
} from "../../../api/object/CoObjectGeneral";
import { mapDocumentPartIncludeToDocumentPartItem } from "../cpm/map-objects";
import { TgkChangeDocStructureElaborationRequest } from "../../../api/models/tgk-change-doc-structure-elaboration-request";
import { StructureDocumentPart } from "../../../api/models/structure-document-part.ts";
import { store } from "../../store/store";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../../utils/constants/eventChannel";
import { TgkDetachElaborationRequest } from "../../../api/models/tgk-detach-elaboration-request";
import { insertDocumentPart } from "../../../office/WordDocumentPart";

export interface CreateDocumentRequest {
  name: string;
  description: string;
  path: string;
  metadata: DocumentPartMetadata;
}

export interface EditDocumentPartRequest {
  name: string;
  description: string;
  id: string;
  metadata: DocumentPartMetadata;
  multiLinked?: boolean;
}

export interface DocumentItem {
  id?: string;
  name?: string;
  description?: string;
  webServer?: string;
  wordAddress?: string;
  webAddress?: string;
  error?: GenericError[];
}

export interface DocumentPartItem {
  id?: string;
  name?: string;
  description?: string;
  rootPartId?: string;
  hasChildren?: boolean;
  order?: number;
}

interface MapDetachOrDuplicateCommonElabReqObj {
  userId: string;
  documentId: string;
  fatherDocumentId: string;
  documentName: string;
  partOrder: number;
  channelIdToNotify: string;
  driveItemId: string;
  documentPartMetadata: DocumentParameters[];
  tenantId: string;
  siteId: string;
  zipFileName: string;
  clientId: string;
  correlationId: string;
  language: string;
  templateInfo: TemplateInfo;
  locale: string;
  contentControlIdCreated: string;
}

import { createSlice } from "@reduxjs/toolkit";
import { selectCpmUsername, setIxbrlEnabled, setIxbrlSettings } from "../settings/configurations-slice";
import { fetchIxbrlSettings, fetchUpdateIxbrlSettings } from "../../../pages/seahorse/SeahorseUtils";
import { TgkCloneElaborationRequest } from "../../../api/models/tgk-clone-elaboration-request";
import { DownloadDocumentStructure } from "../../../pages/document/DownloadCopyPage/DownloadCopyPage";

const documentSlice = createSlice({
  name: "document",
  initialState: {},
  reducers: {
    // your reducers here
  },
});

export default documentSlice.reducer; // <-- Important: default export the reducer

export const fetchCreateDocumentAsync = createAsyncThunk<
  DocumentItem,
  {
    documentName: string;
    documentDescription: string;
    path: string;
    metadata: DocumentPartMetadata;
  }
>("document/fetchCreateDocumentAsync", async (input) => {
  let request: CreateDocumentRequest = {
    name: normalizeDocumentName(input.documentName),
    description: input.documentDescription,
    path: normalizePath(input.path),
    metadata: input.metadata,
  };
  const documentItem: DocumentItem = await fetchCreatedDocument(request);
  if (documentItem.id) {
    const ixbrlEnabled = store.getState().configurationState.ixbrlEnabled;
    fetchUpdateIxbrlSettings(documentItem.id, ixbrlEnabled).then(() => {
      fetchIxbrlSettings().then((result) => store.dispatch(setIxbrlSettings(result)));
    });
    store.dispatch(setIxbrlEnabled(false));
  }

  // eslint-disable-next-line no-undef
  openDocument(documentItem.wordAddress);
  return documentItem;
});

export const editDocumentMetadataAsync = async (input: {
  name: string;
  description: string;
  id: string;
  metadata: DocumentPartMetadata;
  multiLinked: boolean;
  fatherPartId: string | undefined;
}) => {
  let request: EditDocumentPartRequest = {
    name: input.name,
    description: input.description,
    id: input.id,
    metadata: input.metadata,
    multiLinked: input.multiLinked,
  };

  try {
    const documentItem: DocumentPart = await editDocumentPart(request);
    // eslint-disable-next-line no-undef
    NotificationManager.addNotification({
      key: EDIT_DOCUMENT_PROPERTIES_NOTIFICATION,
      message: formatMessage("cof.documentproperties.message"),
      title: null,
      type: "success",
      closable: true,
      removeOnClose: true,
      isTemporary: true,
    });

    if (input.fatherPartId) {
      const coObjectsDocumentPartInclude = await getCoObjectsDocumentPartInclude(input.fatherPartId, documentItem.id);
      const contentControlIds = coObjectsDocumentPartInclude.map((coObject) => parseInt(coObject.contentControlId));
      contentControlIds.forEach((contentControlId) => {
        updateContentControlName(contentControlId, input.name);
      });
    }
    let coObjects = await getCoObjectListToUpdate(input.fatherPartId);
    for (const coObject of coObjects) {
      await editXMLPart(coObject.contentControlId, mapDocumentPartIncludeToDocumentPartItem(coObject));
    }

    return documentItem;
  } catch (e) {
    // eslint-disable-next-line no-undef
    console.log(e);
  }
};

export const unlinkDocumentPartAsync = async (input: { linkedPartId: string; order: number; docPartId: string }) => {
  try {
    const contentControlId = await unlinkDocumentPart(input.docPartId, input.linkedPartId, input.order);
    deleteXMLPart(contentControlId);
    await deleteContentControlById(contentControlId);
    let coObjects = await getCoObjectListToUpdate(input.docPartId);
    for (const coObject of coObjects) {
      await editXMLPart(coObject.contentControlId, mapDocumentPartIncludeToDocumentPartItem(coObject));
    }
    NotificationManager.addNotification({
      key: INSERT_OBJECT_NOTIFICATION,
      message: formatMessage("cof.createnewdocument.message.unlinksuccess"),
      title: null,
      type: "success",
      closable: true,
      removeOnClose: true,
      isTemporary: true,
    });
  } catch (e) {
    // eslint-disable-next-line no-undef
    console.log(e);
  }
};

export const deleteDocumentPartAsync = async (input: {
  linkedPartId: string;
  docPartId: string;
  deleteFromClient?: boolean;
}) => {
  try {
    const contentControlIds: string[] = await deleteDocumentPart(
      input.docPartId,
      input.linkedPartId,
      input.deleteFromClient
    );

    for (let contentControlId of contentControlIds) {
      deleteXMLPart(contentControlId);
      await deleteContentControlById(contentControlId);
      if (input.deleteFromClient) {
        let coObjects = await getCoObjectListToUpdate(input.docPartId);
        for (const coObject of coObjects) {
          await editXMLPart(coObject.contentControlId, mapDocumentPartIncludeToDocumentPartItem(coObject));
        }
      }
    }

    NotificationManager.addNotification({
      key: INSERT_OBJECT_NOTIFICATION,
      message: formatMessage("cof.createnewdocument.message.deletesuccess"),
      title: null,
      type: "success",
      closable: true,
      removeOnClose: true,
      isTemporary: true,
    });
  } catch (e) {
    // eslint-disable-next-line no-undef
    console.log(e);
  }
};

export const deleteDocumentRootAsync = async (input: { docRootId: string; currentDocumentPartId: string }) => {
  try {
    const contentControlIds: string[] = await deleteDocumentRoot(input.docRootId, input.currentDocumentPartId);

    for (let contentControlId of contentControlIds) {
      await deleteXMLPart(contentControlId);
      await deleteContentControlById(contentControlId);
    }

    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessage("cof.createnewdocument.message.deletesuccess"),
      title: "",
      type: "success",
      closable: true,
      removeOnClose: true,
      isTemporary: true,
    });
  } catch (e) {
    // eslint-disable-next-line no-undef
    console.log(e);
    return Promise.reject(e);
  }
};

export function mapTgkChangeStructureElaborationRequest(data: {
  structureDocumentPart: StructureDocumentPart;
  correlationId: string;
  userId: string;
  documentId: string;
  channelIdToNotify: string;
}): TgkChangeDocStructureElaborationRequest {
  return {
    channelIdToNotify: data.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
    clientId: store.getState().configurationState.clientId,
    correlationId: data.correlationId,
    userId: data.userId,
    structureDocumentPart: data.structureDocumentPart,
    typeElaborationRequest: "tgkChangeDocStructureElaborationRequest",
    documentId: data.documentId,
  };
}

export function mapTgkDuplicateElaborationRequest(data: {
  userId: string;
  documentId: string;
  sharepointPath: string;
  tenantId: string;
  siteId: string;
  correlationId: string;
  documentName: string;
  documentPartMetadata: DocumentParameters[];
  advanced: AdvancedDocumentMetadata;
  language: string;
  channelIdToNotify: string;
  duplicateAttachments: boolean;
}): TgkDuplicateElaborationRequest {
  return {
    customerAzureTenantId: data.tenantId,
    channelIdToNotify: data.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
    clientId: store.getState().configurationState.clientId,
    correlationId: data.correlationId,
    userId: data.userId,
    documentId: data.documentId,
    sharepointPath: data.sharepointPath,
    siteId: data.siteId,
    contentControlId: null,
    documentName: data.documentName,
    documentPartMetadata: {
      documentId: data.documentId,
      parametersMetadata: data.documentPartMetadata,
      id: data.advanced.id,
      formatImage: {
        imageExtension: data.advanced.imageExtension,
        imageResolution: data.advanced.imageResolution,
      },
      formatMetadata: {
        numberFormat: { format: data.advanced.numberFormat },
        numberType: data.advanced.typeNumber,
        percentageFormat: { format: data.advanced.percentageFormat },
        percentageType: data.advanced.typePercentage,
        dateFormat: { format: data.advanced.dateFormat },
        dateType: data.advanced.typeDate,
        choice: data.advanced.choice,
        locale: data.advanced.locale,
      },
      templateInfo: data.advanced.templateInfo,
      language: data.language,
    },
    typeElaborationRequest: "tgkDuplicateElaborationRequest",
    duplicateAttachments: data.duplicateAttachments,
  };
}

export function mapTgkImpExpElaborationRequest(data: {
  userId: string;
  documentId: string;
  channelIdToNotify: string;
  driveItemId: string;
  sharepointPath: string;
  elaborationType: "IMPORT" | "EXPORT";
  tenantId: string;
  siteId: string;
  zipFileName: string;
  clientId: string;
  correlationId: string;
}): TgkImpExpElaborationRequest {
  return {
    customerAzureTenantId: data.tenantId,
    clientId: store.getState().configurationState.clientId,
    correlationId: data.correlationId,
    userId: data.userId,
    documentId: data.documentId,
    channelIdToNotify: data.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
    driveItemId: data.driveItemId,
    sharepointPath: data.sharepointPath,
    elaborationType: data.elaborationType,
    siteId: data.siteId,
    zipFileName: data.zipFileName,
    contentControlId: null,
    typeElaborationRequest: "tgkImpExpElaborationRequest",
  };
}

export function mapDetachOrDuplicateCommonElabReqObj(
  data: MapDetachOrDuplicateCommonElabReqObj
): Omit<
  TgkDetachElaborationRequest | TgkCloneElaborationRequest,
  "elaborationType" | "typeElaborationRequest" | "sharepointPath"
> {
  return {
    customerAzureTenantId: data.tenantId,
    clientId: store.getState().configurationState.clientId,
    correlationId: data.correlationId,
    documentPartIncludeOrder: data.partOrder,
    fatherDocumentId: data.fatherDocumentId,
    userId: data.userId,
    documentId: data.documentId,
    documentName: data.documentName,
    channelIdToNotify: data.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
    driveItemId: data.driveItemId,
    contentControlId: data.contentControlIdCreated,
    documentPartMetadata: {
      documentId: data.documentId,
      parametersMetadata: data.documentPartMetadata,
      id: null,
      formatMetadata: {
        numberFormat: null,
        numberType: null,
        percentageFormat: null,
        percentageType: null,
        dateFormat: null,
        dateType: null,
        choice: null,
        locale: data.locale,
      },
      formatImage: null,
      language: data.language,
      templateInfo: data.templateInfo,
    },
    siteId: data.siteId,
    zipFileName: data.zipFileName,
  };
}

export function mapTgkDetachElaborationRequest(
  data: MapDetachOrDuplicateCommonElabReqObj & { sharepointPath: string }
): TgkDetachElaborationRequest {
  return {
    ...mapDetachOrDuplicateCommonElabReqObj(data),
    sharepointPath: data.sharepointPath,
    elaborationType: "DETACH",
    typeElaborationRequest: "tgkDetachElaborationRequest",
  };
}

export function mapTgkCloneElaborationRequest(
  data: MapDetachOrDuplicateCommonElabReqObj & { documentNameSuffix: string }
): TgkCloneElaborationRequest {
  return {
    ...mapDetachOrDuplicateCommonElabReqObj(data),
    documentNameSuffix: data.documentNameSuffix,
    elaborationType: "CLONE",
    typeElaborationRequest: "tgkCloneElaborationRequest",
  };
}

export const exportDocumentRootAsync = async (input: {
  channelIdToNotify: string;
  documentId: string;
  username: string;
  tenantId: string;
  sharepointPath: string;
  dispatch: Dispatch<any>;
  backendUrl: string;
  clientId: string;
}) => {
  try {
    await exportDocumentRoot(
      mapTgkImpExpElaborationRequest({
        userId: input.username,
        documentId: input.documentId,
        channelIdToNotify: input.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
        driveItemId: null,
        sharepointPath: input.sharepointPath,
        elaborationType: "EXPORT",
        tenantId: input.tenantId,
        siteId: null,
        zipFileName: null,
        clientId: input.clientId,
        correlationId: uuid.v4(),
      })
    );
  } catch (e) {
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessage("cof.elaboration.error.export.message"),
      title: "",
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  }
};

export const changeDocumentPartStructureAsync = async (input: {
  username: string;
  structureDocumentPart: StructureDocumentPart;
  documentId: string;
  dispatch: Dispatch<any>;
  backendUrl: string;
  clientId: string;
  channelIdToNotify: string;
}) => {
  try {
    await fetchChangeDocumentPartStructure(
      mapTgkChangeStructureElaborationRequest({
        channelIdToNotify: input.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
        userId: input.username,
        correlationId: uuid.v4(),
        structureDocumentPart: input.structureDocumentPart,
        documentId: input.documentId,
      })
    );
  } catch (e) {
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessage("cof.elaboration.changedocumentstructure.message.failed"),
      title: formatMessage("cof.elaboration.changedocumentstructure.title.failed"),
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  }
};

export const duplicateDocumentRootAsync = async (input: {
  documentId: string;
  channelIdToNotify: string;
  username: string;
  tenantId: string;
  sharepointPath: string;
  dispatch: Dispatch<any>;
  backendUrl: string;
  documentPartMetadata: DocumentParameters[];
  documentName: string;
  advanced: AdvancedDocumentMetadata;
  language: string;
  clientId: string;
  duplicateAttachments: boolean;
}) => {
  try {
    await duplicateDocumentRoot(
      mapTgkDuplicateElaborationRequest({
        userId: input.username,
        documentId: input.documentId,
        sharepointPath: input.sharepointPath,
        tenantId: input.tenantId,
        siteId: null,
        correlationId: uuid.v4(),
        documentPartMetadata: input.documentPartMetadata,
        documentName: input.documentName,
        advanced: input.advanced,
        language: input.language,
        channelIdToNotify: input.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
        duplicateAttachments: input.duplicateAttachments,
      })
    );
  } catch (e) {
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessage("cof.elaboration.error.duplicate.message"),
      title: "",
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  }
};

export const importDocumentRootAsync = async (input: {
  username: string;
  tenantId: string;
  sharepointPath: string;
  zipFileName: string;
  driveItemId: string;
  dispatch: Dispatch<any>;
  backendUrl: string;
  channelIdToNotify?: string;
  siteId: string;
  clientId: string;
}) => {
  const correlationId = uuid.v4();
  try {
    await importDocumentRoot(
      mapTgkImpExpElaborationRequest({
        userId: input.username,
        documentId: correlationId,
        channelIdToNotify: input.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
        driveItemId: input.driveItemId,
        sharepointPath: input.sharepointPath,
        zipFileName: input.zipFileName,
        elaborationType: "IMPORT",
        tenantId: input.tenantId,
        siteId: input.siteId,
        clientId: input.clientId,
        correlationId: correlationId,
      })
    );
  } catch (e) {
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessage("cof.elaboration.error.import.message"),
      title: "",
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  }
};

export function getFormattedPath(input: string) {
  if (input) return input.replace(/>/g, "/");
  else return null;
}

export function normalizePath(path: string) {
  if (path == null || path.length < 1) return "";
  return path.replace(DEFAULT_FOLDER_CREATION + ">", "").replace(/[<>]/g, "/");
}

export const detachMultilinkedDocumentPartAsync = async (input: {
  isDetachWithSingleParent: boolean;
  channelIdToNotify: string;
  documentId: string;
  documentName: string;
  partOrder: number;
  fatherDocumentId: string;
  documentPartMetadata: DocumentParameters[];
  username: string;
  tenantId: string;
  sharepointPath: string;
  dispatch: Dispatch<any>;
  backendUrl: string;
  clientId: string;
  language: string;
  templateInfo: TemplateInfo;
  locale: string;
}) => {
  try {
    const coObjectDocumentPartInclude = await getCoObjectDocumentPartInclude(
      input.fatherDocumentId,
      input.documentId,
      input.partOrder
    );

    let contentControlIdCreated;
    if (!input.isDetachWithSingleParent)
      contentControlIdCreated = await insertDocumentPart(input.documentName, {
        contentControlId: parseInt(coObjectDocumentPartInclude.contentControlId),
        contentControlOrder: input.partOrder,
        relativePosition: "after",
      });

    await detachMultilinkedDocumentPart(
      mapTgkDetachElaborationRequest({
        userId: input.username,
        documentId: input.documentId,
        fatherDocumentId: input.fatherDocumentId,
        documentName: input.documentName,
        partOrder: input.partOrder,
        channelIdToNotify: input.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
        driveItemId: null,
        sharepointPath: input.sharepointPath,
        tenantId: input.tenantId,
        siteId: null,
        documentPartMetadata: input.documentPartMetadata,
        zipFileName: null,
        clientId: input.clientId,
        correlationId: uuid.v4(),
        language: input.language,
        templateInfo: input.templateInfo,
        locale: input.locale,
        contentControlIdCreated,
      })
    );
  } catch (e) {
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessage("cof.elaboration.error.detach.message"),
      title: "",
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  }
};

export const cloneDocumentPartAsync = async (input: {
  channelIdToNotify: string;
  documentId: string;
  documentName: string;
  documentNameSuffix: string;
  partOrder: number;
  fatherDocumentId: string;
  documentPartMetadata: DocumentParameters[];
  username: string;
  tenantId: string;
  dispatch: Dispatch<any>;
  backendUrl: string;
  clientId: string;
  language: string;
  templateInfo: TemplateInfo;
  locale: string;
}) => {
  try {
    const coObjectItem = await getCCByDocPartId(input.fatherDocumentId, input.documentId);

    let contentControlIdCreated = await insertDocumentPart(input.documentName, {
      contentControlId: parseInt(coObjectItem.contentControlId),
      relativePosition: "after",
      contentControlOrder: input.partOrder,
    });

    await cloneDocumentPart(
      mapTgkCloneElaborationRequest({
        userId: input.username,
        documentId: input.documentId,
        fatherDocumentId: input.fatherDocumentId,
        documentName: input.documentName,
        documentNameSuffix: input.documentNameSuffix,
        partOrder: input.partOrder + 1,
        channelIdToNotify: input.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
        driveItemId: null,
        tenantId: input.tenantId,
        siteId: null,
        documentPartMetadata: input.documentPartMetadata,
        zipFileName: null,
        clientId: input.clientId,
        correlationId: uuid.v4(),
        language: input.language,
        templateInfo: input.templateInfo,
        locale: input.locale,
        contentControlIdCreated,
      })
    );
  } catch (e) {
    NotificationManager.addNotification({
      key: CLONE_ELABORATION,
      message: formatMessage("cof.elaboration.error.clone.message"),
      title: "",
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  }
};

export async function downloadDocumentCopyAsync(data: DownloadDocumentStructure) {
  const storeData = store.getState();
  const clientId = storeData.configurationState.clientId;
  const username = selectCpmUsername(storeData);
  const channel = await retrieveDocumentProp(DocPropKeys.DocumentId);
  // eslint-disable-next-line no-undef
  console.log("channel", channel);
  await downloadDocumentCopyJob({
    clientId,
    userId: username,
    typeElaborationRequest: "TgkDownloadCopyElaborationRequest",
    channelIdToNotify: channel || EVENT_CHANNEL_DEFAULT_TOPIC,
    elaborationType: "DOWNLOADCOPY",
    correlationId: uuid.v4(),
    documentName: data.text,
    ...data,
  });
}
